/**
 * 处理视频通话邀请消息 (LOVE_ROOM_INVITE)
 *
 * 职责：
 * 1. 接收视频通话邀请消息
 * 2. 解析邀请者信息
 * 3. 显示视频邀请弹窗
 * 4. 处理用户接听/拒绝操作
 *
 * 邀请忽略场景：
 * 1. ✅ 正在支付时：H5 重定向到支付页面，天然隔离，无需处理
 * 2. ✅ 消息去重：30秒内收到重复邀请（基于 timestamp）
 * 3. ✅ 等待接听页面：正在呼叫中时（callStatus === CALLING）
 * 4. ⏸️ 匹配邀请中：暂不实现，等随机匹配功能完成后再处理
 *
 * 消息格式参考：
 * @see /src/common/constants/imCustomMsgTypes/LOVE_ROOM_INVITE.md
 */

import { showVideoInvite, closeVideoInvite } from '@/components/VideoInviteModal'
import { handleVideoCallInvitation } from '@/api/video'
import { showToast } from 'vant'
import agoraManager from '@/managers/AgoraRTCManager'
import router from '@/router'
import { useVideoCallStore, VIDEO_CALL_ROLE } from '@/stores/videoCall'
import { formatJoinMessage } from './utils'
import { t } from '@/locales'
import { track } from '@/utils/tracker'
import { handleCreateTracksError } from '@/utils/createTracksErrorHandler'
import { handleVideoCallError } from '@/utils/videoCallErrorHandler'
import { SERVER_ERROR_CODE } from '@/common/constants'
import { showDiamondRecharge, closeDiamondRecharge } from '@/components/DiamondRecharge'

const handleReject = async (params, videoCallStore) => {
  try {
    // 上报通话结束埋点（拒绝邀请）
    const { live_id } = params

    track('call_1v1_status_change_video_room', {
      live_id,
      status: 'ended',
      duration: 0, // 拒绝邀请时通话时长为0
      end_reason: 'user_refused',
    })

    videoCallStore.resetCall()
    // 调用拒绝接口
    await handleVideoCallInvitation({
      action: handleVideoCallInvitation.ACTION.REJECT, // 1: 拒绝
      ...params
    }, {
      hideLoading: true,
    })

  } catch (error) {
    showToast(error.message)
  }
}

const handleAccept = async (params, message, videoCallStore) => {
  try {
    // 1. 创建本地轨道
    await agoraManager.createTracks({
      video: true,
      audio: true,
    })
  } catch (error) {
    // 使用统一的错误处理函数
    handleCreateTracksError(error)
    videoCallStore.resetCall()
    await agoraManager.destroy()
    return
  }

  let roomInfo;

  try {
    // 2. 调用接听接口，获取 roomInfo
    roomInfo = await handleVideoCallInvitation({
      action: handleVideoCallInvitation.ACTION.ACCEPT,
      ...params
    })
  } catch (error) {
    if (error.code === SERVER_ERROR_CODE.INSUFFICIENT_COINS) {
      showDiamondRecharge()
    } else {
      handleVideoCallError(error)
    }
    videoCallStore.resetCall()
    await agoraManager.destroy()
    return
  }

  if (!roomInfo || !roomInfo.room_id) {
    throw new Error(t('videoCall.error.noRoomInfo'))
  }

  const { member, channel_id, room_id, live_id, free_duration } = roomInfo
  const { rtc_server } = member || {}

  try {
    // 3. 加入 Agora 频道
    await agoraManager.joinChannel({
      channelId: channel_id,
      token: rtc_server.access_token,
      uid: member.member_id,
    })

    // 4. 更新状态：标记为已连接
    videoCallStore.connectCall()

    // 5. 上报通话接通埋点
    const isTrial = free_duration > 0
    track('call_1v1_status_change_video_room', {
      live_id,
      status: 'connected',
      is_trial: isTrial,
    })
    closeVideoInvite()

    // 6. 跳转到视频通话页面
    router.push({
      name: 'video',
      params: { roomId: room_id },
      state: formatJoinMessage(message),
    })
  } catch (error) {
    console.error('[handleAccept] Failed to answer call:', error)
    showToast(error.message || t('videoCall.error.answerFailed'))
    videoCallStore.resetCall()

    // 失败时清理资源
    await agoraManager.destroy()
  }
}

function isDuplicateInvite(timestamp1, timestamp2) {
  const timeDiff = Math.abs(timestamp1 - timestamp2)
  // 30秒内视为重复邀请
  return timeDiff < 30 * 1000
}

/**
 * 处理视频通话邀请消息
 * @param {Object} event - EventBus 事件对象
 * @param {Object[]} event.data - IMManager 派发的消息数组
 */
export async function handleVideoCallInvite(event) {
  console.log('[handleVideoCallInvite] event:', event)

  const { data: messages } = event
  if (!Array.isArray(messages) || messages.length === 0) return

  const message = messages[0] || {}
  const { msg_content } = message

  const {
    love_invite_member: loveInviteMember,
    love_room: loveRoom,
    live_id: liveId,
    mode,
    match_type: matchType,
    member_gender: memberGender,
    come_from: comeFrom,
    trace_id: traceId,
    content: customContent,
    notice_description: noticeDescription,
  } = msg_content

  // 验证必要字段
  if (!loveInviteMember) {
    return
  }

  // ============ 邀请忽略逻辑 ============
  const videoCallStore = useVideoCallStore()

  // 场景 2: 消息去重
  // 使用 love_room.timestamp 或 message 的时间戳进行去重
  const inviteTimestamp = loveRoom?.timestamp || Date.now()
  if (isDuplicateInvite(videoCallStore.currentRoomInfo?.timestamp, inviteTimestamp)) {
    return
  }

  // 场景 3: 等待接听页面（正在呼叫中）
  if (videoCallStore.isCalling) {
    // TODO: 上报错误给服务器（H5暂不实现）
    // reportInviteIgnored({ reason: 'waiting_limit', inviterMemberId: loveInviteMember.member_id })
    return
  }

  // 场景 4: 匹配邀请中（随机匹配）- 暂不实现
  // if (videoCallStore.isMatching()) {
  //   console.log('[handleVideoInvite] Ignoring invite: random matching in progress')
  //   return
  // }

  videoCallStore.startCall(msg_content, VIDEO_CALL_ROLE.RECEIVER) // 设置为接听方

  // 构造邀请者信息
  const inviter = {
    id: loveInviteMember.id,
    name: loveInviteMember.nickname || '',
    avatar: loveInviteMember.avatar || '',
    country: {
      flag: loveInviteMember.national_flag,
      name: loveInviteMember.national,
    }
  }

  // 确定邀请文本
  let invitationText = ''
  if (customContent) {
    // 优先使用自定义内容（如预约来电提示）
    invitationText = customContent
  } else if (noticeDescription) {
    // 主播端显示警告描述
    invitationText = noticeDescription
  }

  const params = {
    mode,
    source: matchType,
    come_from: comeFrom,
    target_gender: memberGender,
    trace_id: traceId,
    live_id: liveId,
    target_id: loveInviteMember.id, // 邀请者的加密ID
  }
  // 直接调用函数式 API 显示弹窗
  showVideoInvite({
    inviter,
    invitationText,
    onAccept: () => handleAccept(params, message, videoCallStore),
    onReject: () => handleReject(params, videoCallStore),
    onClose: () => handleReject(params, videoCallStore),
    onTimeout: () => closeDiamondRecharge(),
    onClosed: () => closeDiamondRecharge(),
  })
}
